import validator from "validator";
import { readString } from "react-papaparse";
import { cleanPhoneNumber } from "./index";
import { EMAIL_REGEX } from "./Constants";
import { getJsonApi } from "../modules/AmplifyServices";

export const IMPORT_CSV_HEADERS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "notes",
    "street",
    "city",
    "state",
    "postalCode",
    "country"
];

// Canonical field keys after the raw header has been lowercased and
// stripped of spaces, underscores, dashes, dots, and slashes. This means
// "firstName", "First Name", "FIRSTNAME", "first_name", "first-name",
// "first.name", and "First-name" all resolve to "firstName".
const HEADER_ALIASES = {
    firstname: "firstName",
    fname: "firstName",
    givenname: "firstName",
    lastname: "lastName",
    lname: "lastName",
    surname: "lastName",
    familyname: "lastName",
    email: "email",
    emailaddress: "email",
    emailaddr: "email",
    mail: "email",
    phone: "phone",
    phonenumber: "phone",
    phoneno: "phone",
    mobile: "phone",
    mobilephone: "phone",
    mobilenumber: "phone",
    cell: "phone",
    cellphone: "phone",
    cellnumber: "phone",
    contact: "phone",
    contactnumber: "phone",
    tel: "phone",
    telephone: "phone",
    notes: "notes",
    note: "notes",
    comment: "notes",
    comments: "notes",
    remarks: "notes",
    description: "notes",
    street: "street",
    streetaddress: "street",
    address: "street",
    address1: "street",
    addressline1: "street",
    addressline: "street",
    street1: "street",
    streetname: "street",
    city: "city",
    town: "city",
    locality: "city",
    state: "state",
    province: "state",
    region: "state",
    stateprovince: "state",
    provinceorstate: "state",
    stateorprovince: "state",
    postalcode: "postalCode",
    postal: "postalCode",
    zip: "postalCode",
    zipcode: "postalCode",
    postcode: "postalCode",
    country: "country",
    countryname: "country"
};

const stripSeparators = (str) =>
    String(str || "")
        .trim()
        .toLowerCase()
        .replace(/[\s_\-./]+/g, "");

export const CSV_TEMPLATE = `firstName,lastName,email,phone,notes,street,city,state,postalCode,country
Jane,Doe,jane.doe@example.com,+14155552671,Prefers morning appointments,500 Terry A Francois Blvd,San Francisco,CA,94158,USA
John,Smith,john.smith@example.com,+14155552672,,,,,,`;

export const downloadCsvTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "client-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const toTitleCase = (str) =>
    str
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ");

const normalizePhone = (raw) => {
    if (!raw) return "";
    const cleaned = cleanPhoneNumber(String(raw).trim());
    if (!cleaned) return "";
    if (cleaned.startsWith("+")) return cleaned;
    return `+${cleaned}`;
};

const normalizeHeader = (h) => {
    const key = stripSeparators(h);
    return HEADER_ALIASES[key] || key;
};

const trimOrEmpty = (v) => (v ? String(v).trim() : "");

export const ADDRESS_FIELDS = ["street", "city", "state", "postalCode", "country"];
export const REQUIRED_ADDRESS_FIELDS = ["street", "city", "country"];

export const hasAnyAddressField = (row) =>
    ADDRESS_FIELDS.some((f) => row[f]);

export const buildAddressOneLine = (row) => {
    const { street, city, state, postalCode, country } = row;
    const stateZip = [state, postalCode].filter(Boolean).join(" ");
    return [street, city, stateZip, country].filter(Boolean).join(", ");
};

export const normalizeRow = (raw) => ({
    firstName: raw.firstName ? toTitleCase(String(raw.firstName).trim()) : "",
    lastName: raw.lastName ? toTitleCase(String(raw.lastName).trim()) : "",
    email: raw.email ? String(raw.email).trim().toLowerCase() : "",
    phone: normalizePhone(raw.phone),
    notes: raw.notes ? String(raw.notes).trim() : "",
    street: trimOrEmpty(raw.street),
    city: raw.city ? toTitleCase(String(raw.city).trim()) : "",
    state: trimOrEmpty(raw.state),
    postalCode: trimOrEmpty(raw.postalCode).toUpperCase(),
    country: raw.country ? toTitleCase(String(raw.country).trim()) : ""
});

export const validateRow = (row, seenEmails) => {
    const errors = [];
    if (!row.firstName) errors.push("First name is required");
    if (!row.lastName) errors.push("Last name is required");
    if (!row.email) {
        errors.push("Email is required");
    } else if (!EMAIL_REGEX.test(row.email) || !validator.isEmail(row.email)) {
        errors.push("Invalid email format");
    }
    if (!row.phone) {
        errors.push("Phone is required");
    } else if (row.phone.length < 12) {
        errors.push("Phone must include country code (e.g. +1...)");
    }

    const isDuplicate =
        !!row.email && seenEmails && seenEmails.has(row.email);
    if (isDuplicate) errors.push("Duplicate email in this CSV");

    // Address is optional. If any address field is present, require the
    // core fields (street + city + country) so we can geocode it.
    if (hasAnyAddressField(row)) {
        const missing = REQUIRED_ADDRESS_FIELDS.filter((f) => !row[f]);
        if (missing.length) {
            errors.push(`Address missing: ${missing.join(", ")}`);
        }
    }

    return { errors, isDuplicate };
};

export const parseClientsCsv = (csvString) => {
    const { data, errors: parseErrors } = readString(csvString, {
        header: true,
        skipEmptyLines: true,
        transformHeader: normalizeHeader
    });

    const seenEmails = new Set();
    const rows = (data || []).map((raw, idx) => {
        const normalized = normalizeRow(raw);
        const { errors, isDuplicate } = validateRow(normalized, seenEmails);
        if (normalized.email && !isDuplicate) {
            seenEmails.add(normalized.email);
        }
        return {
            id: `row-${idx}`,
            rowNumber: idx + 2, // +2: 1 for header row, 1 for 1-based index
            original: raw,
            ...normalized,
            errors,
            status: errors.length ? "invalid" : "pending"
        };
    });

    return { rows, parseErrors: parseErrors || [] };
};

export const geocodeAddress = async (row) => {
    const oneLine = buildAddressOneLine(row);
    if (!oneLine) return { success: false, error: "Empty address" };
    try {
        const encoded = encodeURIComponent(oneLine);
        const res = await getJsonApi(
            "mapdirection",
            `/geolocation/${encoded}`
        );
        if (res?.success && res.geolocation) {
            const { lat, lng } = res.geolocation;
            if (typeof lat === "number" && typeof lng === "number") {
                return {
                    success: true,
                    latitude: lat,
                    longitude: lng,
                    addressOneLine: oneLine
                };
            }
        }
        return { success: false, error: "Could not locate address" };
    } catch (e) {
        console.log("geocodeAddress error", e);
        return { success: false, error: "Geocoding request failed" };
    }
};

export const buildClientPayload = ({
    row,
    company,
    mbxUser,
    sendWelcomeEmail
}) => {
    const createdBy = `${mbxUser?.firstname || ""} ${
        mbxUser?.lastname || ""
    }`.trim();
    const payload = {
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        phone: row.phone,
        sendWelcomeEmail,
        notes: row.notes || "",
        companyId: company?.id,
        currency: company?.currency || "CAD",
        role: "CLIENT",
        active: true,
        notesDetails: {
            notes: row.notes || "",
            createdBy,
            updatedBy: createdBy
        }
    };

    // Only attach address when geocoding succeeded and we have lat/lng.
    if (
        row.geocode?.success &&
        typeof row.geocode.latitude === "number" &&
        typeof row.geocode.longitude === "number"
    ) {
        payload.address = row.geocode.addressOneLine;
        payload.addressAttributes = {
            street: row.street,
            city: row.city,
            state: row.state,
            country: row.country,
            postalcode: row.postalCode,
            longitude: row.geocode.longitude,
            latitude: row.geocode.latitude
        };
    }

    return payload;
};

export const buildErrorCsv = (rows) => {
    const header = [...IMPORT_CSV_HEADERS, "error"].join(",");
    const escape = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows
        .map((r) =>
            [
                r.firstName,
                r.lastName,
                r.email,
                r.phone,
                r.notes,
                r.street,
                r.city,
                r.state,
                r.postalCode,
                r.country,
                r.errors?.join("; ") ||
                    r.apiMessage ||
                    r.geocode?.error ||
                    "Failed"
            ]
                .map(escape)
                .join(",")
        )
        .join("\n");
    return `${header}\n${body}`;
};

export const downloadErrorCsv = (rows) => {
    const csv = buildErrorCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "client-import-errors.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
